package com.rosario.juridico.marketplace.viewmodel

import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.marketplace.model.Abogado
import com.rosario.juridico.marketplace.model.Consulta
import com.rosario.juridico.marketplace.model.Propuesta
import com.rosario.juridico.marketplace.model.NivelVerificacion
import com.rosario.juridico.marketplace.billing.BillingProvider
import com.rosario.juridico.domain.PublicarConsultaRepository
import com.rosario.juridico.domain.PublicarConsultaResult
import com.rosario.juridico.domain.ConsultaDataModel
import com.rosario.juridico.domain.PerfilAbogadoRepository
import com.rosario.juridico.domain.PerfilAbogadoDataModel
import com.rosario.juridico.domain.PerfilAbogadoResult
import com.rosario.juridico.domain.OfertasRepository
import com.rosario.juridico.domain.OfertasResult
import com.rosario.juridico.domain.MisConsultasClienteRepository
import com.rosario.juridico.domain.MisConsultasResult
import com.rosario.juridico.domain.OfertaDataModel
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.domain.OnboardingRepository
import com.rosario.juridico.domain.OnboardingResult
import com.rosario.juridico.domain.DirectorioAbogadosRepository
import com.rosario.juridico.domain.DirectorioResult
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed class InitState {
    object Loading : InitState()
    object Unauthenticated : InitState()
    object AuthenticatedAndComplete : InitState()
    object NeedsOnboarding : InitState()
}

sealed class UiState<out T> {
    object Idle : UiState<Nothing>()
    object Loading : UiState<Nothing>()
    data class Success<T>(val data: T) : UiState<T>()
    data class Error(val message: String) : UiState<Nothing>()
}

class MarketplaceViewModel(
    private val billingProvider: BillingProvider? = null,
    private val publicarConsultaRepository: PublicarConsultaRepository = PublicarConsultaRepository(),
    private val perfilAbogadoRepository: PerfilAbogadoRepository = PerfilAbogadoRepository(),
    private val ofertasRepository: OfertasRepository = OfertasRepository(),
    private val misConsultasRepository: MisConsultasClienteRepository = MisConsultasClienteRepository(),
    private val onboardingRepository: OnboardingRepository = OnboardingRepository(),
    private val directorioRepository: DirectorioAbogadosRepository = DirectorioAbogadosRepository(),
) {
    private val scope = CoroutineScope(Dispatchers.Main)

    private val _directorioAbogadosState = MutableStateFlow<UiState<List<PerfilAbogadoDataModel>>>(UiState.Idle)
    val directorioAbogadosState: StateFlow<UiState<List<PerfilAbogadoDataModel>>> = _directorioAbogadosState

    private val _filtroEspecialidadDirectorio = MutableStateFlow("Todas")
    val filtroEspecialidadDirectorio: StateFlow<String> = _filtroEspecialidadDirectorio.asStateFlow()

    private val _filtroLocalidadDirectorio = MutableStateFlow("Todas")
    val filtroLocalidadDirectorio: StateFlow<String> = _filtroLocalidadDirectorio.asStateFlow()

    private val _queryBusquedaDirectorio = MutableStateFlow("")
    val queryBusquedaDirectorio: StateFlow<String> = _queryBusquedaDirectorio.asStateFlow()

    private val _misConsultasClienteState = MutableStateFlow<UiState<List<ConsultaDataModel>>>(UiState.Idle)
    val misConsultasClienteState: StateFlow<UiState<List<ConsultaDataModel>>> = _misConsultasClienteState

    private val _ofertasRecibidasState = MutableStateFlow<UiState<List<OfertaDataModel>>>(UiState.Idle)
    val ofertasRecibidasState: StateFlow<UiState<List<OfertaDataModel>>> = _ofertasRecibidasState

    private val _enviarOfertaState = MutableStateFlow<UiState<String>>(UiState.Idle)
    val enviarOfertaState: StateFlow<UiState<String>> = _enviarOfertaState

    private val _enviarMensajeState = MutableStateFlow<UiState<String>>(UiState.Idle)
    val enviarMensajeState: StateFlow<UiState<String>> = _enviarMensajeState

    private val _perfilAbogadoState = MutableStateFlow<UiState<PerfilAbogadoDataModel?>>(UiState.Idle)
    val perfilAbogadoState: StateFlow<UiState<PerfilAbogadoDataModel?>> = _perfilAbogadoState

    private val _guardarPerfilState = MutableStateFlow<UiState<Unit>>(UiState.Idle)
    val guardarPerfilState: StateFlow<UiState<Unit>> = _guardarPerfilState

    private val _publicarConsultaState = MutableStateFlow<UiState<String>>(UiState.Idle)
    val publicarConsultaState: StateFlow<UiState<String>> = _publicarConsultaState

    private val _feedConsultasState = MutableStateFlow<UiState<List<ConsultaDataModel>>>(UiState.Idle)
    val feedConsultasState: StateFlow<UiState<List<ConsultaDataModel>>> = _feedConsultasState

    private val _filtroTipoConsulta = MutableStateFlow("cliente")
    val filtroTipoConsulta: StateFlow<String> = _filtroTipoConsulta

    fun setFiltroTipoConsulta(tipo: String) {
        if (_filtroTipoConsulta.value != tipo) {
            _filtroTipoConsulta.value = tipo
            loadConsultasAbiertas()
        }
    }

    private val _consultasPendientesState = MutableStateFlow<UiState<List<Consulta>>>(UiState.Idle)
    val consultasPendientesState: StateFlow<UiState<List<Consulta>>> = _consultasPendientesState

    private val _perfilesPendientesState = MutableStateFlow<UiState<List<PerfilAbogadoDataModel>>>(UiState.Idle)
    val perfilesPendientesState: StateFlow<UiState<List<PerfilAbogadoDataModel>>> = _perfilesPendientesState

    private val _propuestasState = MutableStateFlow<UiState<List<Propuesta>>>(UiState.Success(emptyList()))
    val propuestasState: StateFlow<UiState<List<Propuesta>>> = _propuestasState

    private val _abogadoState = MutableStateFlow<UiState<Abogado?>>(UiState.Idle)
    val abogadoState: StateFlow<UiState<Abogado?>> = _abogadoState

    private val _currentUserAbogado = MutableStateFlow<Abogado?>(null)
    val currentUserAbogado: StateFlow<Abogado?> = _currentUserAbogado

    private val _isLoadingPerfil = MutableStateFlow(true)
    val isLoadingPerfil = _isLoadingPerfil.asStateFlow()

    private val _abogadosDestacados = MutableStateFlow<List<Abogado>>(emptyList())
    val abogadosDestacados: StateFlow<List<Abogado>> = _abogadosDestacados

    private val _isSubscribed = MutableStateFlow(false)
    val isSubscribed: StateFlow<Boolean> = _isSubscribed.asStateFlow()

    /**
     * Única Fuente de Verdad reactiva para el estado de suscripción y acceso del abogado profesional.
     * Consolida internamente BillingProvider, AppUsageManager.userStatus y el perfil de Firestore.
     */
    val isUsuarioAbogadoSuscrito: StateFlow<Boolean> = isSubscribed

    private val _appInitState = MutableStateFlow<InitState>(InitState.Loading)
    val appInitState: StateFlow<InitState> = _appInitState.asStateFlow()

    private val _isAuthInitializing = MutableStateFlow(true)
    val isAuthInitializing: StateFlow<Boolean> = _isAuthInitializing.asStateFlow()

    private val _isLoggedIn = MutableStateFlow(FirebaseProxy.isLoggedIn())
    val isLoggedIn: StateFlow<Boolean> = _isLoggedIn

    private val _isEmailVerified = MutableStateFlow(FirebaseProxy.isEmailVerified())
    val isEmailVerified: StateFlow<Boolean> = _isEmailVerified

    private val _onboardingState = MutableStateFlow<UiState<Unit>>(UiState.Idle)
    val onboardingState: StateFlow<UiState<Unit>> = _onboardingState

    init {
        observeAuthState()
        observeSubscription()
        observeAppUsageManager()
        loadAbogadosDestacados()
    }

    private fun observeAuthState() {
        scope.launch {
            FirebaseProxy.observeAuthState().collect { uid ->
                val loggedIn = !uid.isNullOrBlank() && !FirebaseProxy.isAnonymous()
                _isLoggedIn.value = loggedIn
                _isEmailVerified.value = FirebaseProxy.isEmailVerified()

                if (!loggedIn || uid.isNullOrBlank()) {
                    _currentUserAbogado.value = null
                    _isLoadingPerfil.value = false
                    _isAuthInitializing.value = false
                    _appInitState.value = InitState.Unauthenticated
                } else {
                    _appInitState.value = InitState.Loading
                    try {
                        val db = Firebase.firestore
                        val doc = db.collection("abogados").document(uid).get()

                        if (doc.exists) {
                            val mat = try { doc.get<String?>("matricula") } catch (_: Exception) { null }
                            val tel = try { doc.get<String?>("telefono") } catch (_: Exception) { null }
                            val nom = try { doc.get<String?>("nombre") } catch (_: Exception) { null }
                            val ape = try { doc.get<String?>("apellido") } catch (_: Exception) { null }
                            val email = try { doc.get<String?>("email") } catch (_: Exception) { null }
                            val sub = try { doc.get<Boolean?>("isSubscribed") } catch (_: Exception) { false }

                            val finalMat = mat.orEmpty()
                            val finalTel = tel.orEmpty()

                            val abogado = Abogado(
                                id = uid,
                                nombre = nom.orEmpty().ifBlank { "Abogado" },
                                apellido = ape.orEmpty(),
                                email = email.orEmpty(),
                                telefono = finalTel,
                                matricula = finalMat,
                                isSubscribed = sub ?: false
                            )

                            _currentUserAbogado.value = abogado
                            recalcularSuscripcion()

                            if (finalMat.isNotBlank()) {
                                _appInitState.value = InitState.AuthenticatedAndComplete
                            } else {
                                loadCurrentUserAbogadoSuspend()
                                if (!_currentUserAbogado.value?.matricula.isNullOrBlank()) {
                                    _appInitState.value = InitState.AuthenticatedAndComplete
                                } else {
                                    _appInitState.value = InitState.NeedsOnboarding
                                }
                            }
                        } else {
                            loadCurrentUserAbogadoSuspend()
                            if (!_currentUserAbogado.value?.matricula.isNullOrBlank()) {
                                _appInitState.value = InitState.AuthenticatedAndComplete
                            } else {
                                _currentUserAbogado.value = null
                                _appInitState.value = InitState.NeedsOnboarding
                            }
                        }
                    } catch (e: Exception) {
                        println("Error consultando 'abogados/$uid': ${e.message}")
                        loadCurrentUserAbogadoSuspend()
                        if (!_currentUserAbogado.value?.matricula.isNullOrBlank()) {
                            _appInitState.value = InitState.AuthenticatedAndComplete
                        } else {
                            _appInitState.value = InitState.NeedsOnboarding
                        }
                    } finally {
                        _isLoadingPerfil.value = false
                        _isAuthInitializing.value = false
                    }
                }
            }
        }
    }

    private fun recalcularSuscripcion() {
        val appPremium = AppUsageManager.instance.userStatus.value.isPremium
        val billingSub = billingProvider?.isSubscribed?.value ?: false
        val abogadoSub = _currentUserAbogado.value?.isSubscribed ?: false
        _isSubscribed.value = appPremium || billingSub || abogadoSub
    }

    // Llamar cada vez que el usuario vuelve de la pantalla de login
    fun refreshAuthState() {
        val loggedIn = FirebaseProxy.isLoggedIn()
        val verified = FirebaseProxy.isEmailVerified()
        _isLoggedIn.value = loggedIn
        _isEmailVerified.value = verified
        
        if (loggedIn) {
            loadCurrentUserAbogado()
        } else {
            _currentUserAbogado.value = null
            recalcularSuscripcion()
        }
    }

    private fun observeSubscription() {
        billingProvider?.let { provider ->
            scope.launch {
                provider.isSubscribed.collect { subscribed ->
                    recalcularSuscripcion()
                    updateSubscriptionInFirestore(subscribed)
                }
            }
        }
    }

    private fun observeAppUsageManager() {
        scope.launch {
            AppUsageManager.instance.userStatus.collect {
                recalcularSuscripcion()
            }
        }
    }

    private fun updateSubscriptionInFirestore(subscribed: Boolean) {
        val uid = FirebaseProxy.getCurrentUserId() ?: _currentUserAbogado.value?.id ?: return
        scope.launch {
            try {
                FirebaseProxy.updateSubscription(uid, subscribed)
                _currentUserAbogado.value = _currentUserAbogado.value?.copy(isSubscribed = subscribed)
            } catch (e: Exception) {}
        }
    }

    suspend fun loadCurrentUserAbogadoSuspend() {
        val uid = FirebaseProxy.getCurrentUserId()
        if (uid == null) {
            _currentUserAbogado.value = null
            recalcularSuscripcion()
            _isLoadingPerfil.value = false
            return
        }
        _isLoadingPerfil.value = true
        try {
            var abogado = FirebaseProxy.getAbogado(uid)
            val perfilResult = perfilAbogadoRepository.obtenerPerfil(uid)
            val perfil = (perfilResult as? PerfilAbogadoResult.Success)?.data

            val perfMatricula = perfil?.matricula.orEmpty()
            val perfTelefono = perfil?.telefono.orEmpty()
            val abogMatricula = abogado?.matricula.orEmpty()
            val abogTelefono = abogado?.telefono.orEmpty()

            val finalMatricula = abogMatricula.ifBlank { perfMatricula }
            val finalTelefono = abogTelefono.ifBlank { perfTelefono }

            val perfNivel = perfil?.nivelVerificacion.orEmpty()
            val abogNivel = abogado?.nivelVerificacion.orEmpty()
            // Priorizar perfiles_abogados/{uid} como fuente primaria rectora para nivelVerificacion
            val finalNivel = perfNivel.ifBlank { abogNivel }.ifBlank { NivelVerificacion.NIVEL_1_CREADA.name }

            if (finalMatricula.isNotBlank() || finalTelefono.isNotBlank() || perfil != null) {
                abogado = (abogado ?: Abogado(id = uid)).copy(
                    matricula = finalMatricula,
                    telefono = finalTelefono,
                    nombre = abogado?.nombre?.ifBlank { perfil?.nombreCompleto.orEmpty() } ?: perfil?.nombreCompleto.orEmpty(),
                    nivelVerificacion = finalNivel
                )
            }
            if (abogado != null) {
                _currentUserAbogado.value = abogado
                recalcularSuscripcion()
            }
        } catch (e: Exception) {
            println("Error crítico deserializando perfil de abogado: ${e.message}")
        } finally {
            _isLoadingPerfil.value = false
        }
    }

    fun loadCurrentUserAbogado() {
        scope.launch {
            loadCurrentUserAbogadoSuspend()
        }
    }

    fun loadAbogadosDestacados() {
        scope.launch {
            try {
                _abogadosDestacados.value = FirebaseProxy.getAbogadosDestacados()
            } catch (e: Exception) {}
        }
    }

    fun startSubscription() {
        billingProvider?.startSubscriptionFlow("juridico_rosario_premium")
    }

    fun loadConsultasPendientes() {
        scope.launch {
            _consultasPendientesState.value = UiState.Loading
            try {
                val consultas = FirebaseProxy.getConsultasPendientes()
                _consultasPendientesState.value = UiState.Success(consultas)
            } catch (e: Exception) {
                _consultasPendientesState.value = UiState.Error(e.message ?: "Error")
            }
        }
    }

    fun aprobarConsulta(consultaId: String) {
        scope.launch {
            try {
                FirebaseProxy.aprobarConsulta(consultaId)
                loadConsultasPendientes()
                loadConsultasAbiertas()
            } catch (e: Exception) {}
        }
    }

    fun rechazarConsulta(consultaId: String, motivo: String) {
        scope.launch {
            try {
                FirebaseProxy.rechazarConsulta(consultaId, motivo)
                loadConsultasPendientes()
            } catch (e: Exception) {}
        }
    }

    fun eliminarConsulta(consultaId: String) {
        scope.launch {
            try {
                FirebaseProxy.eliminarConsulta(consultaId)
                loadConsultasPendientes()
                loadConsultasAbiertas()
            } catch (e: Exception) {}
        }
    }

    fun loadPerfilesPendientes() {
        scope.launch {
            _perfilesPendientesState.value = UiState.Loading
            when (val result = perfilAbogadoRepository.obtenerPerfilesPendientes()) {
                is PerfilAbogadoResult.Success -> {
                    _perfilesPendientesState.value = UiState.Success(result.data)
                }
                is PerfilAbogadoResult.Error -> {
                    _perfilesPendientesState.value = UiState.Error(result.message)
                }
            }
        }
    }

    fun aprobarPerfilAbogado(uid: String) {
        scope.launch {
            when (val result = perfilAbogadoRepository.aprobarPerfilAbogado(uid)) {
                is PerfilAbogadoResult.Success -> {
                    loadPerfilesPendientes()
                }
                is PerfilAbogadoResult.Error -> {
                    println("Error aprobando abogado: ${result.message}")
                }
            }
        }
    }

    fun registrarAbogado(abogado: Abogado, esCliente: Boolean = false) {
        scope.launch {
            _abogadoState.value = UiState.Loading

            val uid = FirebaseProxy.getCurrentUserId()
            if (uid.isNullOrBlank() || FirebaseProxy.isAnonymous()) {
                _abogadoState.value = UiState.Error("Debes estar autenticado con tu cuenta para registrar tu perfil profesional.")
                return@launch
            }

            try {
                val finalAbogado = abogado.copy(id = uid, isSubscribed = _isSubscribed.value)
                FirebaseProxy.saveAbogado(finalAbogado)
                _currentUserAbogado.value = finalAbogado
                _abogadoState.value = UiState.Success(finalAbogado)
            } catch (e: Exception) {
                _abogadoState.value = UiState.Error(e.message ?: "Error de permisos al guardar el perfil en Firestore.")
            }
        }
    }

    fun loadPropuestas(consultaId: String) {
        scope.launch {
            _propuestasState.value = UiState.Loading
            try {
                val propuestas = FirebaseProxy.getPropuestas(consultaId)
                _propuestasState.value = UiState.Success(propuestas)
            } catch (e: Exception) {
                _propuestasState.value = UiState.Error(e.message ?: "Error")
            }
        }
    }

    fun aceptarPropuesta(consultaId: String) {
        scope.launch {
            try {
                FirebaseProxy.aceptarPropuesta(consultaId)
            } catch (e: Exception) {}
        }
    }

    /**
     * Helper de verificación de Trust & Safety: determina si el usuario cuenta con Nivel 3 Verificado.
     */
    fun isUsuarioVerificadoNivel3(): Boolean {
        val currentAbogadoNivel = _currentUserAbogado.value?.nivelVerificacion
        val perfilNivel = (_perfilAbogadoState.value as? UiState.Success<*>)?.data?.let {
            (it as? PerfilAbogadoDataModel)?.nivelVerificacion
        }
        return currentAbogadoNivel == NivelVerificacion.NIVEL_3_VERIFICADO.name ||
                perfilNivel == NivelVerificacion.NIVEL_3_VERIFICADO.name
    }

    /**
     * Ejecuta la publicación de una nueva consulta jurídica coincidiendo con el modelo NoSQL de producción.
     * Soporta tanto el flujo B2C (Clientes) como el B2B (Red de Colegas / Interconsultas).
     */
    fun enviarConsultaLegal(
        descripcion: String,
        telefono: String = "",
        ubicacion: String = "",
        urgencia: String = "Media",
        tipoConsulta: String? = null,
        subtipoB2B: String? = null,
        jurisdiccionDestino: String? = null,
        modalidadHonorarios: String? = null
    ) {
        scope.launch {
            _publicarConsultaState.value = UiState.Loading
            val tipoFinal = tipoConsulta ?: _filtroTipoConsulta.value

            // 🔒 TRUST & SAFETY: Sellar la publicación B2B requiriendo Nivel 3 Verificado
            if (tipoFinal == "colegiado" && !isUsuarioVerificadoNivel3()) {
                _publicarConsultaState.value = UiState.Error(
                    "Se requiere matrícula verificada (Nivel 3 Verificado) para publicar en la Red de Colegas B2B."
                )
                return@launch
            }

            val catFinal = if (tipoFinal == "colegiado") "Interconsulta B2B" else "General"

            when (val result = publicarConsultaRepository.publicarConsultaLegal(
                categoria = catFinal,
                descripcion = descripcion,
                telefono = telefono,
                ubicacion = ubicacion,
                urgencia = urgencia,
                tipoConsulta = tipoFinal,
                subtipoB2B = subtipoB2B,
                jurisdiccionDestino = jurisdiccionDestino,
                modalidadHonorarios = modalidadHonorarios
            )) {
                is PublicarConsultaResult.Success -> {
                    _publicarConsultaState.value = UiState.Success(result.consultaId)
                    loadConsultasAbiertas()
                }
                is PublicarConsultaResult.Error -> {
                    _publicarConsultaState.value = UiState.Error(result.message)
                }
            }
        }
    }

    /**
     * Carga los abogados verificados (NIVEL_3_VERIFICADO) para el Directorio Público (AVVO).
     */
    fun loadDirectorioAbogados() {
        scope.launch {
            _directorioAbogadosState.value = UiState.Loading
            val esp = _filtroEspecialidadDirectorio.value
            val loc = _filtroLocalidadDirectorio.value
            val q = _queryBusquedaDirectorio.value

            when (val result = directorioRepository.getAbogadosVerificados(
                especialidad = if (esp == "Todas") null else esp,
                localidad = if (loc == "Todas") null else loc,
                queryBusqueda = if (q.isBlank()) null else q
            )) {
                is DirectorioResult.Success -> {
                    _directorioAbogadosState.value = UiState.Success(result.data)
                }
                is DirectorioResult.Error -> {
                    _directorioAbogadosState.value = UiState.Error(result.message)
                }
            }
        }
    }

    fun setFiltroEspecialidadDirectorio(esp: String) {
        _filtroEspecialidadDirectorio.value = esp
        loadDirectorioAbogados()
    }

    fun setFiltroLocalidadDirectorio(loc: String) {
        _filtroLocalidadDirectorio.value = loc
        loadDirectorioAbogados()
    }

    fun setQueryBusquedaDirectorio(query: String) {
        _queryBusquedaDirectorio.value = query
        loadDirectorioAbogados()
    }

    fun resetPublicarConsultaState() {
        _publicarConsultaState.value = UiState.Idle
    }

    /**
     * Carga el feed de consultas abiertas desde Firestore ordenadas por fecha reciente.
     */
    fun loadConsultasAbiertas() {
        scope.launch {
            _feedConsultasState.value = UiState.Loading
            try {
                val consultas = publicarConsultaRepository.getConsultasAbiertas(_filtroTipoConsulta.value)
                _feedConsultasState.value = UiState.Success(consultas)
            } catch (e: Exception) {
                _feedConsultasState.value = UiState.Error(
                    e.message ?: "Error al obtener las publicaciones del Marketplace.",
                )
            }
        }
    }

    /**
     * Carga el perfil del abogado profesional desde 'perfiles_abogados' en Firestore.
     */
    fun cargarPerfilAbogado(uid: String? = null) {
        scope.launch {
            _perfilAbogadoState.value = UiState.Loading
            when (val result = perfilAbogadoRepository.obtenerPerfil(uid)) {
                is PerfilAbogadoResult.Success -> {
                    _perfilAbogadoState.value = UiState.Success(result.data)
                }
                is PerfilAbogadoResult.Error -> {
                    _perfilAbogadoState.value = UiState.Error(result.message)
                }
            }
        }
    }

    /**
     * Guarda o actualiza el perfil público del abogado en Firestore.
     */
    fun guardarPerfilAbogado(
        nombreCompleto: String,
        matricula: String,
        jurisdiccion: String,
        especialidades: List<String>,
        biografia: String,
    ) {
        scope.launch {
            _guardarPerfilState.value = UiState.Loading
            val perfil = PerfilAbogadoDataModel(
                nombreCompleto = nombreCompleto,
                matricula = matricula,
                jurisdiccion = jurisdiccion,
                especialidades = especialidades,
                biografia = biografia,
            )
            when (val result = perfilAbogadoRepository.guardarPerfil(perfil)) {
                is PerfilAbogadoResult.Success -> {
                    _guardarPerfilState.value = UiState.Success(Unit)
                    cargarPerfilAbogado()
                }
                is PerfilAbogadoResult.Error -> {
                    _guardarPerfilState.value = UiState.Error(result.message)
                }
            }
        }
    }

    fun resetGuardarPerfilState() {
        _guardarPerfilState.value = UiState.Idle
    }

    /**
     * Envia un mensaje inicial (Buzón Ciego) a una consulta específica en Firestore.
     */
    fun enviarMensaje(
        consultaId: String, 
        mensaje: String, 
        matricula: String, 
        telefono: String,
        intencion: String? = null
    ) {
        scope.launch {
            _enviarMensajeState.value = UiState.Loading
            when (val result = ofertasRepository.enviarMensajeOferta(
                consultaId, mensaje, matricula, telefono, intencion
            )) {
                is com.rosario.juridico.domain.OfertasResult.Success -> {
                    _enviarMensajeState.value = UiState.Success(result.data)
                }
                is com.rosario.juridico.domain.OfertasResult.Error -> {
                    _enviarMensajeState.value = UiState.Error(result.message)
                }
            }
        }
    }

    fun resetEnviarMensajeState() {
        _enviarMensajeState.value = UiState.Idle
    }

    /**
     * Actualiza el rol del usuario durante el Onboarding.
     */
    fun actualizarRolUsuario(rol: String, matricula: String = "", telefono: String = "", onSuccess: () -> Unit = {}) {
        scope.launch {
            _onboardingState.value = UiState.Loading
            when (val result = onboardingRepository.actualizarRolUsuario(rol, matricula, telefono)) {
                is OnboardingResult.Success -> {
                    loadCurrentUserAbogadoSuspend()
                    _appInitState.value = InitState.AuthenticatedAndComplete
                    _onboardingState.value = UiState.Success(Unit)
                    onSuccess()
                }
                is OnboardingResult.Error -> {
                    _onboardingState.value = UiState.Error(result.message)
                }
            }
        }
    }

    /**
     * Actualiza el rol del usuario durante el Onboarding síncronamente devolviendo Result
     * garantizando la descarga atómica previa de _currentUserAbogado antes de retornar Success.
     */
    suspend fun actualizarRolUsuarioSync(rol: String, matricula: String = "", telefono: String = ""): Result<Unit> {
        val uid = FirebaseProxy.getCurrentUserId()
        if (uid.isNullOrBlank() || FirebaseProxy.isAnonymous()) {
            val errorMsg = "Sesión no válida o no autenticada en Firebase. Inicia sesión primero para guardar tu perfil."
            _onboardingState.value = UiState.Error(errorMsg)
            return Result.failure(Exception(errorMsg))
        }

        _onboardingState.value = UiState.Loading
        return when (val result = onboardingRepository.actualizarRolUsuario(rol, matricula, telefono)) {
            is OnboardingResult.Success -> {
                loadCurrentUserAbogadoSuspend()
                _appInitState.value = InitState.AuthenticatedAndComplete
                _onboardingState.value = UiState.Success(Unit)
                Result.success(Unit)
            }
            is OnboardingResult.Error -> {
                _onboardingState.value = UiState.Error(result.message)
                Result.failure(Exception(result.message))
            }
        }
    }

    fun resetOnboardingState() {
        _onboardingState.value = UiState.Idle
    }

    /**
     * Resetea el rol del usuario para permitirle cambiar de perfil (Cliente/Abogado).
     */
    fun resetearRolUsuario() {
        scope.launch {
            onboardingRepository.resetearRolUsuario()
        }
    }

    /**
     * Permite reconfigurar o cambiar el perfil profesional sin cerrar la sesión de Firebase Auth.
     * Limpia únicamente el estado local del profesional y ajusta el estado a 'NeedsOnboarding'.
     */
    fun cambiarProfesional(onComplete: () -> Unit = {}) {
        _currentUserAbogado.value = null
        _appInitState.value = InitState.NeedsOnboarding
        onComplete()
    }

    /**
     * Cierra la sesión activa en Firebase y limpia los estados de sesión del ViewModel.
     */
    fun limpiarSesion(onComplete: () -> Unit = {}) {
        scope.launch {
            try {
                FirebaseProxy.logout()
            } catch (e: Exception) {
                println("Error al cerrar sesión: ${e.message}")
            } finally {
                _currentUserAbogado.value = null
                _isLoggedIn.value = false
                _isEmailVerified.value = false
                _isSubscribed.value = false
                _appInitState.value = InitState.Unauthenticated
                onComplete()
            }
        }
    }

    /**
     * Envia una propuesta/oferta económica a una consulta específica en Firestore.
     */
    fun enviarOfertaProfesional(consultaId: String, montoPropuesto: Double, mensaje: String) {
        scope.launch {
            _enviarOfertaState.value = UiState.Loading
            when (val result = ofertasRepository.enviarOferta(consultaId, montoPropuesto, mensaje)) {
                is OfertasResult.Success -> {
                    _enviarOfertaState.value = UiState.Success(result.data)
                }
                is OfertasResult.Error -> {
                    _enviarOfertaState.value = UiState.Error(result.message)
                }
            }
        }
    }

    fun resetEnviarOfertaState() {
        _enviarOfertaState.value = UiState.Idle
    }

    /**
     * Carga las publicaciones propias del cliente autenticado desde Firestore.
     */
    fun loadMisConsultasCliente() {
        scope.launch {
            _misConsultasClienteState.value = UiState.Loading
            when (val result = misConsultasRepository.getMisConsultas()) {
                is MisConsultasResult.Success -> {
                    _misConsultasClienteState.value = UiState.Success(result.data)
                }
                is MisConsultasResult.Error -> {
                    _misConsultasClienteState.value = UiState.Error(result.message)
                }
            }
        }
    }

    /**
     * Carga la subcolección de ofertas/presupuestos recibidos para una consulta específica.
     */
    fun loadOfertasRecibidas(consultaId: String) {
        scope.launch {
            _ofertasRecibidasState.value = UiState.Loading
            when (val result = misConsultasRepository.getOfertasDeConsulta(consultaId)) {
                is MisConsultasResult.Success -> {
                    _ofertasRecibidasState.value = UiState.Success(result.data)
                }
                is MisConsultasResult.Error -> {
                    _ofertasRecibidasState.value = UiState.Error(result.message)
                }
            }
        }
    }
}